import React from 'react';
import { Card, Button, Badge, DataTable, Input, Modal } from '../../../components/ui';
import { Plus, Clock, Power, PauseCircle, Pencil, XCircle, Trash2 } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';

interface BackendSorteo {
  id: string;
  tipo_id: string;
  nombre: string;
  fecha: string;
  hora_apertura: string;
  hora_cierre: string;
  estado: 'ABIERTO' | 'CERRADO' | 'INACTIVO';
  es_recurrente: boolean;
  frecuencia: 'UNICO' | 'DIARIO' | 'DIAS_ESPECIFICOS';
  dias_semana?: string | null;
  fecha_fin?: string | null;
  auto_activar: boolean;
  auto_cerrar: boolean;
  eliminado?: boolean;
  es_extranjera?: boolean;
  auto_stamp_ok?: boolean;
  auto_stamp_error?: string | null;
  auto_stamp_locales_afectados?: string[];
  requiere_reaplicacion_paquetes?: boolean;
}

interface SorteoForm {
  tipo_id: string;
  nombre: string;
  hora_apertura: string;
  hora_cierre: string;
  frecuencia: 'DIARIO' | 'DIAS_ESPECIFICOS';
  dias_semana: number[];
  es_extranjera: boolean;
}

const DIAS_SEMANA_OPCIONES = [
  { value: 0, label: 'L' },
  { value: 1, label: 'M' },
  { value: 2, label: 'K' },
  { value: 3, label: 'J' },
  { value: 4, label: 'V' },
  { value: 5, label: 'S' },
  { value: 6, label: 'D' },
];

const parseDiasSemana = (diasSemana?: string | null): number[] => {
  if (!diasSemana) return [];
  return diasSemana
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item) && item >= 0 && item <= 6)
    .filter((item, index, arr) => arr.indexOf(item) === index)
    .sort((a, b) => a - b);
};

const TIPOS_SORTEO = [
  { id: 'NORMAL', nombre: 'Normal' },
  { id: 'REVENTADO', nombre: 'Reventado' },
  { id: 'MONAZOS', nombre: 'Monazos' },
  { id: 'FECHAS', nombre: 'Fechas' },
  { id: 'PARLEY', nombre: 'Parley' },
];

const defaultForm = (): SorteoForm => ({
  tipo_id: 'NORMAL',
  nombre: '',
  hora_apertura: '00:00',
  hora_cierre: '23:59',
  frecuencia: 'DIARIO',
  dias_semana: [],
  es_extranjera: false,
});

const normalizarHoraComparable = (valor?: string | null): string => {
  if (!valor) return '';
  const texto = valor.trim();
  if (!texto) return '';
  const partes = texto.split(':');
  const hh = (partes[0] || '00').padStart(2, '0');
  const mm = (partes[1] || '00').padStart(2, '0');
  return `${hh}:${mm}:00`;
};

const normalizarHoraInput = (valor?: string | null): string => {
  if (!valor) return '00:00';
  const texto = valor.trim();
  if (!texto) return '00:00';
  const partes = texto.split(':');
  const hh = (partes[0] || '00').padStart(2, '0');
  const mm = (partes[1] || '00').padStart(2, '0');
  return `${hh}:${mm}`;
};

const normalizarNombre = (valor?: string | null): string => (valor || '').trim();

const normalizarDiasSemanaComparable = (diasSemana?: string | null): string | null => {
  const dias = parseDiasSemana(diasSemana);
  return dias.length ? dias.join(',') : null;
};

const formatFrecuencia = (s: BackendSorteo) => {
  if (s.frecuencia === 'DIARIO' || !s.frecuencia || s.frecuencia === 'UNICO') return 'Diario';
  if (s.frecuencia === 'DIAS_ESPECIFICOS' && s.dias_semana) {
    const dias = s.dias_semana
      .split(',')
      .map((d) => DIAS_SEMANA_OPCIONES.find((o) => o.value === Number(d.trim()))?.label || '?');
    return dias.join(' ');
  }
  return 'Diario';
};

export const SorteosPage: React.FC = () => {
  const [sorteos, setSorteos] = React.useState<BackendSorteo[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [warning, setWarning] = React.useState('');
  const [tipoFiltro, setTipoFiltro] = React.useState('');
  const [modalOpen, setModalOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [editing, setEditing] = React.useState<BackendSorteo | null>(null);
  const [form, setForm] = React.useState<SorteoForm>(defaultForm());
  const [confirmEliminar, setConfirmEliminar] = React.useState<{ id: string; nombre: string } | null>(null);

  // Split: activos vs desactivados
  const activos = sorteos.filter((s) => s.auto_activar || s.auto_cerrar);
  const desactivados = sorteos.filter((s) => !s.auto_activar && !s.auto_cerrar);

  const loadSorteos = React.useCallback(async () => {
    setLoading(true);
    setError('');
    setWarning('');
    try {
      const params = new URLSearchParams();
      if (tipoFiltro) params.append('tipo_id', tipoFiltro);
      const data = await fetchAPI<BackendSorteo[]>(`/sorteos?${params.toString()}`);
      setSorteos(data);
    } catch (e: any) {
      setError(e.message || 'Error al cargar sorteos');
      setSorteos([]);
    } finally {
      setLoading(false);
    }
  }, [tipoFiltro]);

  React.useEffect(() => {
    loadSorteos();
  }, [loadSorteos]);

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm());
    setModalOpen(true);
  };

  const openEdit = (s: BackendSorteo) => {
    setEditing(s);
    setForm({
      tipo_id: s.tipo_id,
      nombre: s.nombre,
      hora_apertura: normalizarHoraInput(s.hora_apertura),
      hora_cierre: normalizarHoraInput(s.hora_cierre),
      frecuencia: s.frecuencia === 'DIAS_ESPECIFICOS' ? 'DIAS_ESPECIFICOS' : 'DIARIO',
      dias_semana: parseDiasSemana(s.dias_semana),
      es_extranjera: !!s.es_extranjera,
    });
    setModalOpen(true);
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setWarning('');
    try {
      if (form.frecuencia === 'DIAS_ESPECIFICOS' && form.dias_semana.length === 0) {
        setError('Debe seleccionar al menos un dia de la semana');
        setSaving(false);
        return;
      }

      const payload: any = {
        tipo_id: form.tipo_id,
        nombre: form.nombre,
        hora_apertura: form.hora_apertura,
        hora_cierre: form.hora_cierre,
        es_recurrente: true,
        frecuencia: form.frecuencia,
        dias_semana: form.frecuencia === 'DIAS_ESPECIFICOS'
          ? form.dias_semana.slice().sort((a, b) => a - b).join(',')
          : null,
        fecha_fin: null,
        auto_activar: true,
        auto_cerrar: true,
        es_extranjera: !!form.es_extranjera,
      };

      if (editing) {
        const payloadEdicion: Record<string, unknown> = {};
        const diasSemanaNuevo = form.frecuencia === 'DIAS_ESPECIFICOS'
          ? form.dias_semana.slice().sort((a, b) => a - b).join(',')
          : null;
        const diasSemanaActual = normalizarDiasSemanaComparable(editing.dias_semana);

        const nombreNuevo = normalizarNombre(form.nombre);
        const nombreActual = normalizarNombre(editing.nombre);
        if (nombreNuevo !== nombreActual) {
          payloadEdicion.nombre = nombreNuevo;
        }

        if (normalizarHoraComparable(form.hora_apertura) !== normalizarHoraComparable(editing.hora_apertura)) {
          payloadEdicion.hora_apertura = form.hora_apertura;
        }

        if (normalizarHoraComparable(form.hora_cierre) !== normalizarHoraComparable(editing.hora_cierre)) {
          payloadEdicion.hora_cierre = form.hora_cierre;
        }

        const frecuenciaActual = editing.frecuencia === 'DIAS_ESPECIFICOS' ? 'DIAS_ESPECIFICOS' : 'DIARIO';
        if (form.frecuencia !== frecuenciaActual) {
          payloadEdicion.frecuencia = form.frecuencia;
        }

        if (diasSemanaNuevo !== diasSemanaActual) {
          payloadEdicion.dias_semana = diasSemanaNuevo;
        }

        if (!!form.es_extranjera !== !!editing.es_extranjera) {
          payloadEdicion.es_extranjera = !!form.es_extranjera;
        }

        if (!Object.keys(payloadEdicion).length) {
          setModalOpen(false);
          return;
        }

        await fetchAPI<BackendSorteo>(`/sorteos/${editing.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payloadEdicion),
        });
      } else {
        const result = await fetchAPI<BackendSorteo>('/sorteos', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        if (result.auto_stamp_ok === false) {
          const locales = result.auto_stamp_locales_afectados?.join(', ') || '';
          setWarning(
            `Sorteo creado, pero falló la aplicación automática de paquetes de reglas` +
            (locales ? ` en: ${locales}` : '') +
            `. Ve a Paquetes de Reglas y re-aplica manualmente.`
          );
        }
      }
      setModalOpen(false);
      await loadSorteos();
    } catch (e: any) {
      setError(e.message || 'No se pudo guardar el sorteo');
    } finally {
      setSaving(false);
    }
  };

  const changeEstado = async (sorteoId: string, action: 'activar' | 'desactivar') => {
    setError('');
    try {
      await fetchAPI<BackendSorteo>(`/sorteos/${sorteoId}/${action}`, {
        method: 'POST',
      });
      await loadSorteos();
    } catch (e: any) {
      setError(e.message || 'No se pudo actualizar el estado');
    }
  };

  const cerrarSorteo = async (sorteoId: string) => {
    setError('');
    try {
      await fetchAPI<BackendSorteo>(`/sorteos/${sorteoId}/cerrar`, {
        method: 'POST',
      });
      await loadSorteos();
    } catch (e: any) {
      setError(e.message || 'No se pudo cerrar el sorteo');
    }
  };

  const handleEliminar = async () => {
    if (!confirmEliminar) return;
    setError('');
    try {
      await fetchAPI(`/sorteos/${confirmEliminar.id}`, { method: 'DELETE' });
      setConfirmEliminar(null);
      await loadSorteos();
    } catch (e: any) {
      setError(e.message || 'No se pudo eliminar el sorteo');
      setConfirmEliminar(null);
    }
  };

  const columnsActivos = [
    {
      key: 'nombre',
      header: 'Sorteo',
      render: (s: BackendSorteo) => <span className="font-medium">{s.nombre}</span>,
    },
    {
      key: 'tipo_id',
      header: 'Tipo',
      render: (s: BackendSorteo) => (
        <div className="flex items-center gap-1">
          <Badge variant="primary">{s.tipo_id}</Badge>
          {s.es_extranjera ? (
            <Badge variant="warning">Extranjera</Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'hora',
      header: 'Horario',
      render: (s: BackendSorteo) => (
        <span className="flex items-center gap-1 text-gray-600">
          <Clock size={14} />
          {s.hora_apertura} - {s.hora_cierre}
        </span>
      ),
    },
    {
      key: 'frecuencia',
      header: 'Frecuencia',
      render: (s: BackendSorteo) => (
        <span className="text-gray-600">{formatFrecuencia(s)}</span>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (s: BackendSorteo) => (
        <Badge variant={s.estado === 'ABIERTO' ? 'success' : s.estado === 'CERRADO' ? 'danger' : ('neutral' as any)}>
          {s.estado}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (s: BackendSorteo) => (
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); openEdit(s); }}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
            title="Editar"
          >
            <Pencil size={16} />
          </button>
          {s.estado === 'ABIERTO' && (
            <button
              onClick={(e) => { e.stopPropagation(); cerrarSorteo(s.id); }}
              className="p-2 text-gray-500 hover:text-danger-600 hover:bg-danger-50 rounded-lg"
              title="Cerrar hoy (se reabre mañana)"
            >
              <XCircle size={16} />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); changeEstado(s.id, 'desactivar'); }}
            className="p-2 text-gray-500 hover:text-warning-600 hover:bg-warning-50 rounded-lg"
            title="Desactivar"
          >
            <PauseCircle size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setConfirmEliminar({ id: s.id, nombre: s.nombre }); }}
            className="p-2 text-gray-500 hover:text-danger-600 hover:bg-danger-50 rounded-lg"
            title="Eliminar sorteo"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  const columnsDesactivados = [
    {
      key: 'nombre',
      header: 'Sorteo',
      render: (s: BackendSorteo) => <span className="font-medium text-gray-500">{s.nombre}</span>,
    },
    {
      key: 'tipo_id',
      header: 'Tipo',
      render: (s: BackendSorteo) => (
        <div className="flex items-center gap-1">
          <Badge variant="neutral">{s.tipo_id}</Badge>
          {s.es_extranjera ? (
            <Badge variant="warning">Extranjera</Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'hora',
      header: 'Horario',
      render: (s: BackendSorteo) => (
        <span className="text-gray-400">{s.hora_apertura} - {s.hora_cierre}</span>
      ),
    },
    {
      key: 'frecuencia',
      header: 'Frecuencia',
      render: (s: BackendSorteo) => (
        <span className="text-gray-400">{formatFrecuencia(s)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (s: BackendSorteo) => (
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); openEdit(s); }}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
            title="Editar"
          >
            <Pencil size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); changeEstado(s.id, 'activar'); }}
            className="p-2 text-gray-500 hover:text-success-600 hover:bg-success-50 rounded-lg"
            title="Activar"
          >
            <Power size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setConfirmEliminar({ id: s.id, nombre: s.nombre }); }}
            className="p-2 text-gray-500 hover:text-danger-600 hover:bg-danger-50 rounded-lg"
            title="Eliminar sorteo"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sorteos</h1>
          <p className="text-gray-500">Gestion de sorteos</p>
        </div>
        <Button variant="primary" leftIcon={<Plus size={18} />} onClick={openCreate}>
          Nuevo Sorteo
        </Button>
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <select
            value={tipoFiltro}
            onChange={(e) => setTipoFiltro(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Todos los tipos</option>
            {TIPOS_SORTEO.map((tipo) => (
              <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
            ))}
          </select>
          <p className="text-sm text-gray-500">
            {activos.length} activos, {desactivados.length} desactivados
          </p>
        </div>
        {error && <p className="mt-3 text-sm text-danger-600">{error}</p>}
        {warning && <p className="mt-3 text-sm text-amber-600 bg-amber-50 p-2 rounded">{warning}</p>}
      </Card>

      {/* Sorteos activos */}
      <Card padding="none">
        <DataTable
          columns={columnsActivos}
          data={activos}
          keyExtractor={(s) => s.id}
          isLoading={loading}
          emptyMessage="No hay sorteos activos"
        />
      </Card>

      {/* Sorteos desactivados */}
      {desactivados.length > 0 && (
        <>
          <h2 className="text-lg font-semibold text-gray-500">Desactivados</h2>
          <Card padding="none">
            <DataTable
              columns={columnsDesactivados}
              data={desactivados}
              keyExtractor={(s) => s.id}
              emptyMessage=""
            />
          </Card>
        </>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar sorteo' : 'Nuevo sorteo'}
        size="lg"
      >
        <form onSubmit={submitForm} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={form.tipo_id}
                onChange={(e) => setForm((prev) => ({ ...prev, tipo_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                disabled={!!editing}
              >
                {TIPOS_SORTEO.map((tipo) => (
                  <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                ))}
              </select>
            </div>
            <Input
              label="Nombre"
              value={form.nombre}
              onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
              required
            />
            <Input
              label="Hora apertura"
              type="time"
              value={form.hora_apertura}
              onChange={(e) => setForm((prev) => ({ ...prev, hora_apertura: e.target.value }))}
              required
            />
            <Input
              label="Hora cierre"
              type="time"
              value={form.hora_cierre}
              onChange={(e) => setForm((prev) => ({ ...prev, hora_cierre: e.target.value }))}
              required
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input
              id="sorteo-extranjera"
              type="checkbox"
              checked={!!form.es_extranjera}
              onChange={(e) => setForm((prev) => ({ ...prev, es_extranjera: e.target.checked }))}
              className="h-4 w-4"
            />
            <label htmlFor="sorteo-extranjera" className="text-sm text-gray-700 select-none">
              Sorteo extranjero
              <span className="block text-xs text-gray-500">
                Aplica la variante "extranjera" del paquete de reglas (comisión, premios y restringidos). Si no existe, se usa la nacional.
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frecuencia</label>
              <select
                value={form.frecuencia}
                onChange={(e) => setForm((prev) => ({
                  ...prev,
                  frecuencia: e.target.value as SorteoForm['frecuencia'],
                  dias_semana: e.target.value === 'DIAS_ESPECIFICOS' ? prev.dias_semana : [],
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="DIARIO">Diario</option>
                <option value="DIAS_ESPECIFICOS">Dias especificos</option>
              </select>
            </div>
            {form.frecuencia === 'DIAS_ESPECIFICOS' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dias</label>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-1 sm:gap-2">
                  {DIAS_SEMANA_OPCIONES.map((dia) => {
                    const checked = form.dias_semana.includes(dia.value);
                    return (
                      <label key={dia.value} className="flex flex-col items-center gap-1 text-sm text-gray-700">
                        <span>{dia.label}</span>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setForm((prev) => {
                            const exists = prev.dias_semana.includes(dia.value);
                            const next = exists
                              ? prev.dias_semana.filter((d) => d !== dia.value)
                              : [...prev.dias_semana, dia.value];
                            return { ...prev, dias_semana: next };
                          })}
                          className="h-4 w-4"
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={saving}>
              {editing ? 'Guardar cambios' : 'Crear sorteo'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de confirmación para eliminar sorteo */}
      <Modal
        isOpen={!!confirmEliminar}
        onClose={() => setConfirmEliminar(null)}
        title="Eliminar sorteo"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            ¿Está seguro que desea eliminar el sorteo{' '}
            <span className="font-semibold">{confirmEliminar?.nombre}</span>?
          </p>
          <p className="text-sm text-gray-500">
            Si el sorteo tiene ventas registradas, será desactivado y ocultado en lugar de eliminarse,
            preservando el historial. Si no tiene ventas, se eliminará permanentemente.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setConfirmEliminar(null)}>
              Cancelar
            </Button>
            <Button type="button" variant="danger" onClick={handleEliminar}>
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SorteosPage;
